import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useUserPreferences } from '../hooks/useUserPreferences';
import EventCard from './EventCard';
import CategoryTiles from './CategoryTiles';
import DateFilter from './DateFilter';
import LocationDisplay from './LocationDisplay';
import './EventsList.css';

interface Event {
  id: string;
  slug: string;
  title: string;
  short_description: string;
  category: string;
  age_min: number;
  age_max: number;
  venue_name: string;
  city: string;
  start_date: string;
  is_free: boolean;
  price_description?: string;
  rating_avg?: number;
  rating_count?: number;
}

const CATEGORIES = [
  { name: 'Arts', emoji: '🎨', color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { name: 'STEM', emoji: '🔬', color: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
  { name: 'Outdoors', emoji: '🌳', color: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
  { name: 'Sports', emoji: '⚽', color: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
  { name: 'Performing Arts', emoji: '🎭', color: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' },
  { name: 'Library', emoji: '📚', color: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' },
];

interface AgeGroup {
  label: string;
  minAge: number;
  maxAge: number;
}

const AGE_GROUPS: AgeGroup[] = [
  { label: 'Toddlers', minAge: 0, maxAge: 2 },
  { label: '3-6 years', minAge: 3, maxAge: 6 },
  { label: '7-10 years', minAge: 7, maxAge: 10 },
  { label: 'Teens', minAge: 11, maxAge: 17 },
];

const EventsList: React.FC = () => {
  const navigate = useNavigate();
  const { preferences } = useUserPreferences();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedContentType, setSelectedContentType] = useState<'events' | 'camps' | 'classes'>('events');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isWeekendFilter, setIsWeekendFilter] = useState(false);
  const [selectedAgeGroups, setSelectedAgeGroups] = useState<AgeGroup[]>([]);

  useEffect(() => {
    fetchEvents();
    if (preferences?.age_ranges && preferences.age_ranges.length > 0) {
      const userAgeGroups = AGE_GROUPS.filter(group =>
        preferences.age_ranges.includes(group.label.split(' ')[0].toLowerCase())
      );
      setSelectedAgeGroups(userAgeGroups);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences, selectedContentType]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const today = new Date().toISOString().split('T')[0];
      
      const tableName = selectedContentType;
      const query = supabase
        .from(tableName)
        .select('*')
        .eq('status', 'approved');
      
      // Handle date filtering based on table structure
      if (tableName === 'events') {
        query.or(`end_date.is.null,end_date.gte.${today}`);
      } else if (tableName === 'camps' || tableName === 'classes') {
        query.or(`end_date.is.null,end_date.gte.${today}`);
      }
      
      query.order('start_date', { ascending: true });

      const { data, error } = await query;

      if (error) throw error;
      
      // Normalize data structure (camps and classes use 'name' instead of 'title')
      const normalizedData = data?.map(item => ({
        ...item,
        title: item.title || item.name,
      })) || [];
      
      setEvents(normalizedData);
    } catch (err: any) {
      console.error('Error fetching events:', err);
      setError(err.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date: Date | null, isWeekend: boolean = false) => {
    setSelectedDate(date);
    setIsWeekendFilter(isWeekend);
  };

  const filterEvents = (events: Event[]) => {
    let filtered = events;
    const selectedLocation = localStorage.getItem('selectedCity');
    if (selectedLocation) {
      filtered = filtered.filter(e => e.city === selectedLocation);
    }
    if (selectedCategory) {
      filtered = filtered.filter(e => e.category === selectedCategory);
    }
    if (selectedAgeGroups.length > 0) {
      filtered = filtered.filter(e => {
        return selectedAgeGroups.some(group =>
          e.age_min <= group.maxAge && e.age_max >= group.minAge
        );
      });
    }
    if (selectedDate) {
      filtered = filtered.filter(e => {
        const eventDate = new Date(e.start_date);
        const eventYear = eventDate.getFullYear();
        const eventMonth = eventDate.getMonth();
        const eventDay = eventDate.getDate();
        const selectedYear = selectedDate.getFullYear();
        const selectedMonth = selectedDate.getMonth();
        const selectedDay = selectedDate.getDate();

        if (isWeekendFilter) {
          const sundayDate = new Date(selectedDate);
          sundayDate.setDate(selectedDate.getDate() + 1);
          return (
            (eventYear === selectedYear && eventMonth === selectedMonth && eventDay === selectedDay) ||
            (eventYear === sundayDate.getFullYear() && eventMonth === sundayDate.getMonth() && eventDay === sundayDate.getDate())
          );
        }
        return eventYear === selectedYear && eventMonth === selectedMonth && eventDay === selectedDay;
      });
    }
    return filtered;
  };

  const groupEventsByTime = (events: Event[]) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const today = new Date(now);
    const endOfWeek = new Date(now);
    endOfWeek.setDate(now.getDate() + (7 - now.getDay()));
    const nextWeek = new Date(endOfWeek);
    nextWeek.setDate(endOfWeek.getDate() + 7);

    const groups: { [key: string]: Event[] } = {
      today: [],
      thisWeek: [],
      nextWeek: [],
      later: [],
    };

    events.forEach(event => {
      const eventDate = new Date(event.start_date);
      eventDate.setHours(0, 0, 0, 0);
      if (eventDate.getTime() === today.getTime()) {
        groups.today.push(event);
      } else if (eventDate > today && eventDate <= endOfWeek) {
        groups.thisWeek.push(event);
      } else if (eventDate > endOfWeek && eventDate <= nextWeek) {
        groups.nextWeek.push(event);
      } else if (eventDate > nextWeek) {
        groups.later.push(event);
      }
    });

    return groups;
  };

  const selectedLocation = localStorage.getItem('selectedCity');
  const locationFilteredEvents = selectedLocation
    ? events.filter(e => e.city === selectedLocation)
    : events;

  const categoryCounts = CATEGORIES.map(cat => ({
    ...cat,
    count: locationFilteredEvents.filter(e => e.category === cat.name).length,
  }));

  const filteredEvents = filterEvents(events);
  const groupedEvents = groupEventsByTime(filteredEvents);

  if (loading) {
    return (
      <div className="events-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading amazing events...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="events-container">
        <div className="error">
          <p>😕 Oops! {error}</p>
          <button onClick={fetchEvents} className="retry-btn">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="events-container">
      <button className="back-button" onClick={() => navigate('/')}>
        ← Back to Home
      </button>
      <LocationDisplay />
      
      <div className="content-type-filter">
        <h3 className="filter-title">Content Type</h3>
        <div className="content-type-buttons">
          <button
            className={`content-type-btn ${selectedContentType === 'events' ? 'active' : ''}`}
            onClick={() => setSelectedContentType('events')}
          >
            {selectedContentType === 'events' && '✓ '}🎉 Events
          </button>
          <button
            className={`content-type-btn ${selectedContentType === 'camps' ? 'active' : ''}`}
            onClick={() => setSelectedContentType('camps')}
          >
            {selectedContentType === 'camps' && '✓ '}🏕️ Camps
          </button>
          <button
            className={`content-type-btn ${selectedContentType === 'classes' ? 'active' : ''}`}
            onClick={() => setSelectedContentType('classes')}
          >
            {selectedContentType === 'classes' && '✓ '}📖 Classes
          </button>
        </div>
      </div>
      
      <CategoryTiles
        categories={categoryCounts}
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
        totalCount={locationFilteredEvents.length}
      />
      <div className="age-filter">
        <h3 className="filter-title">Age Group (select multiple)</h3>
        <div className="age-buttons">
          <button
            className={`age-btn ${selectedAgeGroups.length === 0 ? 'active' : ''}`}
            onClick={() => setSelectedAgeGroups([])}
          >
            All Ages
          </button>
          {AGE_GROUPS.map((ageGroup) => (
            <button
              key={ageGroup.label}
              className={`age-btn ${selectedAgeGroups.includes(ageGroup) ? 'active' : ''}`}
              onClick={() => {
                if (selectedAgeGroups.includes(ageGroup)) {
                  setSelectedAgeGroups(selectedAgeGroups.filter(g => g !== ageGroup));
                } else {
                  setSelectedAgeGroups([...selectedAgeGroups, ageGroup]);
                }
              }}
            >
              {selectedAgeGroups.includes(ageGroup) && '✓ '}{ageGroup.label}
            </button>
          ))}
        </div>
      </div>
      <DateFilter selectedDate={selectedDate} onSelectDate={handleDateSelect} />
      {filteredEvents.length === 0 ? (
        <div className="no-events">
          <p>No events found for your selection. Try different filters!</p>
        </div>
      ) : (
        <>
          {groupedEvents.today.length > 0 && (
            <div className="time-section">
              <h2 className="section-title">🔥 Today</h2>
              <div className="events-list">
                {groupedEvents.today.map(event => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            </div>
          )}
          {groupedEvents.thisWeek.length > 0 && (
            <div className="time-section">
              <h2 className="section-title">📅 This Week</h2>
              <div className="events-list">
                {groupedEvents.thisWeek.map(event => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            </div>
          )}
          {groupedEvents.nextWeek.length > 0 && (
            <div className="time-section">
              <h2 className="section-title">➡️ Next Week</h2>
              <div className="events-list">
                {groupedEvents.nextWeek.map(event => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            </div>
          )}
          {groupedEvents.later.length > 0 && (
            <div className="time-section">
              <h2 className="section-title">🗓️ Coming Soon</h2>
              <div className="events-list">
                {groupedEvents.later.map(event => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EventsList;
